import {
    AmbientLight,
    ArrowHelper,
    BufferAttribute,
    BufferGeometry,
    Color,
    DirectionalLight,
    EdgesGeometry,
    Euler,
    Group,
    HemisphereLight,
    Light,
    LineBasicMaterial,
    LineSegments,
    Mesh,
    MeshBasicMaterial,
    Object3D,
    Sphere,
    Vector3,
} from 'three';
import { wallLength } from './controllers/wallControllers';
import * as dm from './domain'
import logging from './logging';
import { gammaCorrect } from './utils/gammaCorrect';

const log = logging('utils');

/**
 * Like jQuery's ready handler, this will fire when document.readyState
 * transitions to interactive, or it will get enqueued at the end of the
 * current execution if the readyState has already advanced beyond 'loading'.
 */
export const ready = (fn: () => void) => {
    if(document.readyState == 'loading'){
        document.addEventListener('DOMContentLoaded', fn);
    }
    else {
        setTimeout(fn, 0);
    }
}

/**
 * A convenience to look up elements in the current document by id.
 */
export const id = <T = HTMLElement>(x: string) => document.getElementById(x) as T;

/**
 * Show or hide helpers around objects in the 3D scene to aid with visual
 * diagnostics.
 */
export const helpers = (scene: Object3D, heights: number[], toggle: boolean) => {
    if(toggle) {
        // Draw lines at the 32mm offsets that represent where the holes are in
        // the boring panels
        const linesHelper = lines(heights);
        linesHelper.position.x = -72;
        scene.add(linesHelper);
    } else {
        let helpers = new Array();
        scene.traverse((obj) => {
            if(obj.userData.isHelper){
                helpers.push(obj);
            }
        });
        for(let helper of helpers){
            scene.remove(helper);
        }
    }
}
(window as any).helpers = helpers;

export class ThreeAxisHelper extends Object3D {
    constructor(size: number = 6) {
        super();
        this.add(new ArrowHelper(
            new Vector3(1, 0, 0),
            new Vector3(0, 0, 0),
            size,
            0xFF0000
        ));
        this.add(new ArrowHelper(
            new Vector3(0, 1, 0),
            new Vector3(0, 0, 0),
            size,
            0x00FF00
        ));
        this.add(new ArrowHelper(
            new Vector3(0, 0, 1),
            new Vector3(0, 0, 0),
            size,
            0x0000FF
        ));
    }
}

/**
 * Diagnostic function to display everything as a wireframe to make it easier
 * to see if there are misalignment issues.
 */
export const wireframe = (input: Object3D, ) => {
    const color: number = 0x000000;
    const hitColor = gammaCorrect(0x003080);
    input.traverse(obj => {
        if(obj.userData.isSectionHit && obj instanceof Mesh) {
            const lines = new LineSegments(
                obj.geometry,
                new LineBasicMaterial({color: hitColor})
            );
            lines.position.copy(obj.position);
            lines.rotation.copy(obj.rotation);
            lines.scale.copy(obj.scale);
            lines.renderOrder = 2;
            obj.parent.add(lines);
            obj.visible = true;
            obj.material.transparent = true;
            obj.material.opacity = 0.333;
            obj.material.depthWrite = false;
        }
        else if(obj instanceof Mesh && obj.geometry.attributes.position) {
            const edges = new EdgesGeometry(obj.geometry);
            const lines = new LineSegments(
                edges,
                new LineBasicMaterial({color}));
            lines.position.copy(obj.position);
            lines.rotation.copy(obj.rotation);
            lines.scale.copy(obj.scale);
            obj.parent.add(lines);
            obj.visible = false;
        }
    });
}

/**
 * Creates horizontal lines at the given height intervals and returns them as
 * gray line segments.
 */
export const lines = (heights: number[]) => {
    const
        geo = new BufferGeometry(),
        verts = new Float32Array(heights.length * 6),
        mat = new MeshBasicMaterial({ color: gammaCorrect(0xCCCCCC) }),
        lines = new LineSegments(geo, mat);

    for(let i=0, j=0; i < heights.length; i++, j+=6) {
        const
            z = 0,
            y = heights[i];
        verts[j  ] = 0;
        verts[j+1] = y;
        verts[j+2] = z;
        verts[j+3] = 144; // 12 ft
        verts[j+4] = y;
        verts[j+5] = z;
    }

    geo.setAttribute('position', new BufferAttribute(verts, 3));
    lines.userData.isHelper = true;
    return lines;
}

/**
 * Only allow a function to be called once per frame. This could be done with a
 * specific timeout time, but this is using requestAnimationFrame since it's
 * meant to be used with a drag handler.
 */
export const throttle = (fn: () => void) => {
    let first = true;
    return () => {
        if(first) {
            first = false;
            try {
                fn();
            }
            catch(error) {
                log.error(error);
            } finally {
                requestAnimationFrame(() => first = true);
            }
        }
    };
};

/**
 * Defer execution of the function f until currently queued tasks have
 * completed. This is useful if you want to do some processing after the current
 * round of event handlers has finished running.
 * @function defer
 */
export const defer = (f: (args: any[]) => any) => {
    setTimeout(f, 0);
}

/**
 * This metafunction will create a new function that executes the given function
 * on a deferred basis every time, whereas defer immediately executes the given
 * function only once.
 * @function deferred
 */
export const deferred = (f: (args: any[]) => any) => {
    return () => {
        setTimeout(f, 0);
    }
}

/**
 * If you start many async procedures at nearly the same time they are not
 * guaranteed to complete in order. This class helps by dropping the effects of
 * async procedures that finish after a procedure started at a later time.
 */
export class OrderedEffects {
    serial: number = 0;

    async queue<T>(cause: () => Promise<T>, effect: (arg: T) => void) {
        const sequence = this.serial++;

        let result = await cause();

        if(this.serial > sequence+1) {
            // drop the change
        }
        else {
            effect(result);
        }
    }
}

/**
 * This object throttles the queued functions to occurr no more than once per
 * frame. In doing so, it introduces one frame of latency. This means that
 * queued functions will run on the next frame, at the earliest, but that if
 * a second function is queued while waiting for the first, then the first will
 * never run and only the function queued later will run.
 */
export class ThrottledDeferred {
    requestId: number | undefined;

    queue(fn: () => any) {
        if(this.requestId !== undefined) {
            window.cancelAnimationFrame(this.requestId);
        }
        this.requestId = window.requestAnimationFrame(() => {
            this.requestId = undefined;
            fn();
        });
    }
}

interface NoArgConstructor<V> {
    new(): V;
}

/**
 * Default maps take a constructor function as an argument and will return a
 * new instance when getting a non-existent value from the map.
 */
export class DefaultMap<K, V> extends Map<K, V> {

    ctor: NoArgConstructor<V>;

    constructor(ctor: NoArgConstructor<V>) {
        super();
        this.ctor = ctor;
    }

    get(key: K): V {
        if(!this.has(key)) {
            this.set(key, new this.ctor());
        }
        return super.get(key);
    }
}

/**
 * The UpdateManager holds any number of ThrottledDeferred queues, stored by
 * a unique identifier.
 */
export class UpdateManager {
    private pending: DefaultMap<unknown, ThrottledDeferred> =
        new DefaultMap(ThrottledDeferred);

    queue(id: unknown, fn: () => any) {
        this.pending.get(id).queue(fn);
    }
}

/**
 * Returns a function that returns the time since it was last called. It starts
 * at infinity, which is logically correct in some way, but also ridiculous.
 */
export const delta = () => {
    let last = -Infinity;
    return () => {
        const
            now = performance.now(),
            result = now - last;
        last = now;
        return result;
    }
};


/**
 * Convert a string into an HTMLElement
 */
export const html = (str: string) => {
    const elt = document.createElement('template');
    elt.innerHTML = str;
    return elt.content;
}

export const basename = (url: string) => {
    if(url) {
        let elts = url.split('/');
        return elts[elts.length-1];
    } else {
        return null;
    }
}


/**
 * Similar to the range() function in Python, but without the optional step
 * parameter. The direction of iteration is determined automatically. Note that
 * range(3, 0) is not the reverse of range(0, 3) because the first parameter
 * is always inclusive, and the second parameter is always exclusive.
 */
export function* range(start: number, end: number) {
    if(start > end) {
        for(let i = start; i > end; i--) {
            yield i;
        }
    } else {
        for(let i = start; i < end; i++) {
            yield i;
        }
    }
}

/**
 * Iterate over two iterators in tandem, stopping when the shorter is
 * exhausted.
 */
export const zip =
    function*<A, B>(a: Iterable<A>, b: Iterable<B>): Generator<[A, B]>
{
    const ai = a[Symbol.iterator]();
    const bi = b[Symbol.iterator]();

    let ar = ai.next();
    let br = bi.next();
    let done = ar.done || br.done;

    while(!done) {
        yield [ar.value, br.value];

        ar = ai.next();
        br = bi.next();
        done = ar.done || br.done;
    }
}


/**
 * See https://en.wikipedia.org/wiki/Linear_interpolation#Programming_language_support
 * @param a - the value at t=0
 * @param b - the value at t=1
 * @param t - a proportion on the unit interval [0, 1] used to scale
 * linearly between a and b.
 * @returns - a value between a and b determined by the magnitude of
 * t.
 */
export const lerp = (a: number, b: number, t: number) => {
     return (1-t)*a + t*b;
}

/**
 * Creates a conventional Promise that resolves after some number of
 * milliseconds.
 * @param duration - how many milliseconds to wait before resolving.
 * @returns - the promise has no value, it just resolves.
 */
export const timer = (duration: number) => {
    return new Promise( resolve => {
        setTimeout(resolve, duration);
    });
}


// The rest are easing functions: https://easings.net/

export const easeInOutSine = (x: number) => {
    return -(Math.cos(Math.PI * x) - 1) / 2;
}

export const easeInExpo = (x: number) => {
    return x === 0 ? 0 : Math.pow(2, 10 * x - 10);
}

/**
 * Removes the item from the array.
 *
 * *Warning* Don't use this on array properties of DomainModel objects. Use the
 * `removed` version to re-assign the whole array property instead of editing
 * its value in place, e.g. item.sections = removed(section, item.sections);
 * @param item the thing to remove
 * @param a the array to remove it from
 * @returns true if it was removed, false otherwise
 */
export const remove = <T>(item: T, a: Array<T>): boolean => {
    const i = a.indexOf(item);
    if(i > -1) {
        a.splice(i, 1);
        return true;
    }
    return false;
}

/**
 * Returns a copy of the array a with the item removed, or merely a duplicate
 * of the array if the item cannot be found.
 * @param item the thing to remove
 * @param a the array to copy; the item will be removed from the copy
 */
export const removed = <T>(item: T, a: T[]): T[] => {
    const b = Array.from(a);
    remove(item, b);
    return b;
}

/**
 * Replace the old item in the array with the given replacement item.
 *
 * *Warning* Don't use this on array properties of DomainModel objects. Use the
 * `replaced` version to re-assign the whole array property instead of editing
 * its value in place, e.g. item.sections = replaced(old, repl, item.sections);
 * @param old the item to be replaced
 * @param replacement the item to replace the old item with
 * @param a the array to operate on
 * @returns true if the item was replaced, false otherwise
 */
export const replace = <T>(old: T, replacement: T, a: Array<T>): boolean => {
    const i = a.indexOf(old);
    if(i > -1) {
        a.splice(i, 1, replacement);
        return true;
    }
    return false;
}

/**
 * Returns a copy of the array a with the old item replaced with the
 * replacement.
 * @param old the item to be replaced
 * @param replacement the item to replace the old item with
 * @param a the array to operate on
 * @returns a copy of a with the old item replaced with the replacement
 */
export const replaced = <T>(old: T, replacement: T, a: T[]): T[] => {
    const b = Array.from(a);
    replace(old, replacement, b);
    return b;
}

/**
 * Returns a new set that contains only elements that appear in both sets. This
 * operation is symmetrical meaning the order of the `a` and `b` parameters
 * should not matter.
 * @returns a new set containing only the items that appear both in a and b.
 */
export const intersection = <T>(a: Set<T>, b: Set<T>): Set<T> => {
    var result = new Set<T>();
    for(let item of a) {
        if(b.has(item)) result.add(item);
    }
    return result;
}

/**
 * Choose the closest numeric element from a list.
 */
export const select = (x: number, input: number[]): number => {
    const differences = new Array(input.length);
    for(let i=0; i<input.length; i++) {
        differences[i] = Math.abs(input[i] - x);
    }
    return input[mini(differences)];
}

// Find the index of the smallest value. Returns -1 for empty arrays.
export const mini = (xs: number[]): number => {
    let m = Number.POSITIVE_INFINITY;
    let mi = -1;
    for(let i = 0; i < xs.length; i++) {
        if(xs[i] < m) {
            m = xs[i];
            mi = i;
        }
    }
    return mi;
}


export const saveBuffer = (buffer: ArrayBuffer, filename: string) => {
    var blob = new Blob( [ buffer ], { type: 'application/octet-stream' } );
    const link = document.createElement('a');
    link.href = URL.createObjectURL( blob );
    link.download = filename;
    link.click();
}

export const partition = <T>(items: T[], predicate: (T) => boolean): [T[], T[]] =>
{
    let i=0;
    while(i < items.length && predicate(items[i])) {
        i++;
    }
    return [items.slice(0, i), items.slice(i)];
}

/**
 * Iterate through all sequential pairs of an array.
 */
export function* pairwise<T>(iterable: Iterable<T>): Generator<[T, T?]> {
    let last: T = null;
    for(const item of iterable) {
        if(last == null) {
            last = item;
            continue;
        }
        yield [last, item];
        last = item;
    }
}

/**
 * Iterate through something two at a time. On the last iteration, the second
 * element of the tuple is null because there is no next element.
 */
export function* lookahead<T>(iterable: Iterable<T>): Generator<[T, T?]> {
    let last: T = null;
    for(const item of iterable) {
        if(last == null) {
            last = item;
            continue;
        }
        yield [last, item];
        last = item;
    }
    
    if(last) {
        yield [last, null];
    }
}

export function* neighbors<T>(iterable: Iterable<T>):
    Generator< [ T|undefined, T, T|undefined ] >
{
    const iter = iterable[Symbol.iterator]();
    let
        prev = iter.next(),
        curr = iter.next(),
        next = iter.next();
    if(prev.done) return;
    
    yield [null, prev.value, curr.value];

    while(!curr.done) {
        yield [prev.value, curr.value, next.value];
        prev = curr;
        curr = next;
        next = iter.next();
    }
}

export const firstMatching = <T>(iterable: Iterable<T>, p: (t: T) => boolean)
    : T | undefined =>
{
    for(const item of iterable) {
        if(p(item)) return item;
    }
}

export const uuid4 = () => {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    // Set the version bits to indicate version 4
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    // Set the variant bits
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const chrs = bytes.reduce(
        (a, b) => a + b.toString(16).padStart(2, '0'),
        '');

    return (
        chrs.slice( 0,  8) + '-' +
        chrs.slice( 8, 12) + '-' +
        chrs.slice(12, 16) + '-' +
        chrs.slice(16, 20) + '-' +
        chrs.slice(20, 32)
    );
};

/**
 * Randomly re-order the input array.
 */
export const shuffle = (array: unknown[]): void => {
    for(let i = array.length - 1; i > 0; i--)
    {
        const j = Math.floor(Math.random() * i);
        [array[j], array[i]] = [array[i], array[j]]; // swap
    }
}

type Resolver = () => void;

export const promiseWithResolvers = (): [Promise<void>, Resolver, Resolver] => {
    let
        resolve: Resolver,
        reject: Resolver;

    const p = new Promise<void>((resolver, rejecter) => {
        resolve = resolver;
        reject = rejecter;
    });

    return [p, resolve, reject];
}
